import { Link } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { faqItems } from '@/mocks/faq';
import SeoHead from '@/components/feature/SeoHead';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function HomePage() {
  const featuredVideos = videos.filter((v) => v.featured);
  const trendingVideos = videos.slice(0, 8);

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃视频',
    url: 'https://mitaovideo.com',
    description: '蜜桃视频是您发现优质高清视频内容的首选平台，汇聚海量精选视频资源，涵盖生活、娱乐、美食、旅行等多元类目。',
    sameAs: [],
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蜜桃视频',
    url: 'https://mitaovideo.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://mitaovideo.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead
        title="蜜桃视频 - 精选高清视频内容平台 | 在线观看高清视频"
        description="蜜桃视频是您发现优质高清视频内容的首选平台，汇聚海量精选视频资源，涵盖生活、娱乐、美食、旅行等多元类目，画质清晰、加载流畅，随时随地畅享视频乐趣，立即探索蜜桃视频的丰富世界。"
        keywords="蜜桃视频,蜜桃视频高清在线,蜜桃视频平台,蜜桃视频免费观看,蜜桃视频下载,精选短视频平台,高清视频内容聚合"
        canonicalUrl="https://mitaovideo.com/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20minimalist%20living%20room%20with%20warm%20ambient%20lighting%2C%20cozy%20atmosphere%2C%20large%20TV%20screen%20displaying%20beautiful%20nature%20scenery%2C%20soft%20neutral%20beige%20and%20cream%20tones%2C%20scandinavian%20interior%20design%2C%20peaceful%20evening%20mood%2C%20editorial%20photography%2C%20high%20quality%20cinematic%20composition&width=1800&height=900&seq=hero-bg-mitao&orientation=landscape"
          alt="蜜桃视频 - 精选高清视频内容平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
            发现精彩
            <br />
            <span className="text-primary-500">高清视频世界</span>
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
            汇聚海量精选视频资源，涵盖生活、娱乐、美食、旅行等多元类目
            <br className="hidden md:block" />
            画质清晰、加载流畅，随时随地畅享视频乐趣
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即探索
            </Link>
            <Link
              to="/about"
              className="px-6 py-2.5 border border-white/30 text-white rounded-md text-sm font-medium hover:bg-white/10 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">探索视频分类</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              六大精选类目，覆盖你生活的方方面面，找到属于你的精彩内容
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">精选推荐</h2>
              <p className="text-sm text-foreground-600">编辑精心挑选的高品质内容</p>
            </div>
            <Link
              to="/category/all"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {featuredVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full aspect-video overflow-hidden">
                  <img
                    src={video.imageUrl}
                    alt={video.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                    {video.duration}
                  </div>
                  <div className="absolute top-2 left-2">
                    <span className="text-xs bg-primary-500 text-background-50 px-2 py-0.5 rounded-full">
                      {video.categoryName}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                    {video.description}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {formatViews(video.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i> {formatViews(video.likes)}
                    </span>
                    <span className="ml-auto">{video.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">热门推荐</h2>
              <p className="text-sm text-foreground-600">最多人观看的精彩内容</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {trendingVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full aspect-video overflow-hidden">
                  <img
                    src={video.imageUrl}
                    alt={video.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                    {video.duration}
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {formatViews(video.views)}
                    </span>
                    <span>{video.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">为什么选择蜜桃视频</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              我们用心的产品体验，只为给你最好的观影享受
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: 'ri-4k-line', title: '高清画质', desc: '支持4K超清播放，每一帧都清晰细腻，带来沉浸式观影体验' },
              { icon: 'ri-speed-up-line', title: '流畅加载', desc: '领先的视频编码技术，秒开秒播，告别卡顿等待' },
              { icon: 'ri-stack-line', title: '海量精选', desc: '六大类目精心筛选，只呈现最优质的内容资源' },
              { icon: 'ri-device-line', title: '多端适配', desc: 'PC、平板、手机全面适配，随时随地轻松观看' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-4">
                  <i className={`${item.icon} text-xl md:text-2xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">常见问题</h2>
            <p className="text-sm md:text-base text-foreground-600">
              关于蜜桃视频，你想知道的都在这里
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {faqItems.map((item, i) => (
              <details key={i} className="group border border-background-200/70 rounded-lg overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between p-4 md:p-5 bg-background-50 hover:bg-background-100 transition-colors">
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{item.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-primary-500">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">立即开启你的视频之旅</h2>
          <p className="text-sm md:text-base text-background-50/80 mb-6 md:mb-8">
            加入蜜桃视频，发现更多精选高清内容
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="px-6 py-2.5 bg-background-50 text-primary-500 rounded-md text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即探索
            </Link>
            <Link
              to="/about"
              className="px-6 py-2.5 border border-background-50/40 text-background-50 rounded-md text-sm font-medium hover:bg-background-50/10 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              关于我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}